import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { ArrowLeft, Calendar, User, Camera } from 'lucide-react';
import { getNewsById } from '@/lib/utils/news-data';
import { QRCodeHorodaty } from '@/components/ui/app/qr-code';
import { AppleStoreIcon } from '@/components/ui/app/icons/apple-store';
import { GooglePlayIcon } from '@/components/ui/app/icons/google-play';
import { useTranslation } from 'react-i18next';

const formatDate = (epochSeconds) => {
    if (!epochSeconds) return '—';
    try {
        return new Date(Number(epochSeconds) * 1000).toLocaleDateString('fr-FR', {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
        });
    } catch {
        return '—';
    }
};

const markdownComponents = {
    h3: ({ children }) => (
        <h3 className="font-['Poppins',_sans-serif] font-normal text-[18px] text-[#030213] mb-[12px] mt-[24px] first:mt-0">
            {children}
        </h3>
    ),
    p: ({ children }) => (
        <p className="font-['Poppins',_sans-serif] text-[14px] text-[#717182] mb-[12px] leading-relaxed">
            {children}
        </p>
    ),
    ul: ({ children }) => <ul className="space-y-[10px] mb-[12px]">{children}</ul>,
    ol: ({ children }) => <ol className="space-y-[16px] mb-[12px]">{children}</ol>,
    li: ({ children, ordered, index }) => {
        if (ordered) {
            return (
                <li className="flex gap-[12px]">
                    <div className="flex items-center justify-center w-[32px] h-[32px] rounded-full bg-[#34DCA7] shrink-0 mt-[2px]">
                        <span className="font-['Poppins',_sans-serif] text-[14px] text-[#030213] font-medium">
                            {(index ?? 0) + 1}
                        </span>
                    </div>
                    <div className="flex-1 pt-[4px]">
                        <span className="font-['Poppins',_sans-serif] text-[14px] text-[#717182]">{children}</span>
                    </div>
                </li>
            );
        }
        return (
            <li className="flex items-start gap-[10px]">
                <div className="w-[8px] h-[8px] rounded-full bg-[#34DCA7] mt-[6px] shrink-0" />
                <span className="font-['Poppins',_sans-serif] text-[14px] text-[#717182]">{children}</span>
            </li>
        );
    },
    strong: ({ children }) => (
        <strong className="font-['Poppins',_sans-serif] font-semibold text-[#030213]">{children}</strong>
    ),
    blockquote: ({ children }) => (
        <div className="bg-[#34DCA7]/10 border-l-4 border-[#34DCA7] rounded-[8px] p-[16px] mb-[12px]">
            {children}
        </div>
    ),
};

export default function NewsDetail() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { newsId } = useParams();
    const article = useMemo(() => getNewsById(newsId), [newsId]);

    if (!article) {
        return (
            <div className="bg-[#F5F5F5] min-h-full p-[20px]">
                <button
                    onClick={() => navigate(-1)}
                    className="flex items-center justify-center w-[40px] h-[40px] rounded-[8px] bg-[#F5F5F5] border border-[#E5E5E5] text-[#030213] hover:bg-[#E5E5E5] transition-colors mb-4"
                >
                    <ArrowLeft className="h-5 w-5" />
                </button>
                <p className="font-['Poppins',_sans-serif] text-[14px] text-[#717182]">{t('dashboard:articleIntrouvable')}</p>
            </div>
        );
    }

    return (
        <div className="bg-[#F5F5F5] flex flex-col min-h-full">

            {/* ── Mobile sticky header ── */}
            <div className="md:hidden bg-white border-b border-[#E8E8E8] sticky top-0 z-40">
                <div className="px-[20px] py-[18px] flex items-center gap-[12px]">
                    <button
                        onClick={() => navigate(-1)}
                        className="flex items-center justify-center w-[40px] h-[40px] rounded-[8px] bg-[#F5F5F5] border border-[#E5E5E5] text-[#030213] hover:bg-[#E5E5E5] transition-colors shrink-0"
                    >
                        <ArrowLeft className="h-5 w-5" />
                    </button>
                    <h1 className="font-['Poppins',_sans-serif] text-[18px] text-[#030213] truncate">{t('dashboard:actualites')}</h1>
                </div>
            </div>

            {/* ── Desktop sub-header ── */}
            <div className="hidden md:block w-full bg-white border-b border-[#E5E5E5] px-[20px] py-[11px] shrink-0">
                <div className="flex items-center gap-[12px] max-w-[1400px] mx-auto">
                    <button
                        onClick={() => navigate(-1)}
                        className="flex items-center justify-center w-[40px] h-[40px] rounded-[8px] bg-[#F5F5F5] border border-[#E5E5E5] text-[#030213] hover:bg-[#E5E5E5] transition-colors shrink-0"
                    >
                        <ArrowLeft className="h-5 w-5" />
                    </button>
                    <h2 className="font-['Poppins',_sans-serif] text-[20px] text-[#030213] font-semibold line-clamp-1">
                        {article.title}
                    </h2>
                </div>
            </div>

            {/* ── Content ── */}
            <div className="flex-1 pb-[120px] md:pb-[40px]">
                <div className="px-[20px] pt-[24px]">
                    <article className="w-full max-w-[1400px] mx-auto">

                        {/* Hero image */}
                        {article.image_URL && (
                            <div className="relative w-full aspect-video md:aspect-[16/4] rounded-[16px] overflow-hidden mb-[20px]">
                                <img
                                    src={article.image_URL}
                                    alt={article.title}
                                    className="w-full h-full object-cover"
                                />
                            </div>
                        )}

                        <div className="md:flex md:gap-[32px] md:items-start">

                            {/* ── Desktop aside ── */}
                            <aside className="hidden md:block md:w-[300px] md:shrink-0">
                                <div className="sticky top-[20px]">
                                    <div className="bg-[#34DCA7] rounded-[8px] p-[24px]">
                                        <div className="mb-[20px] flex justify-center">
                                            <QRCodeHorodaty size={120} />
                                        </div>
                                        <h3 className="font-['Poppins',_sans-serif] text-[18px] text-[#030213] mb-[8px] text-center">
                                            {t('dashboard:commencezACertifier')}
                                        </h3>
                                        <p className="font-['Poppins',_sans-serif] text-[13px] text-[rgba(3,2,19,0.7)] mb-[16px] text-center">
                                            {t('dashboard:telechargezLApplication')}
                                        </p>
                                        <div className="flex flex-col gap-[8px]">
                                            <a
                                                href="https://apps.apple.com"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="flex items-center justify-center gap-[8px] bg-[#030213] hover:bg-[#030213]/90 transition-colors rounded-[8px] px-[16px] py-[10px]"
                                            >
                                                <AppleStoreIcon className="text-white w-5 h-5" />
                                                <span className="font-['Poppins',_sans-serif] text-[13px] text-white">{t('dashboard:appStore')}</span>
                                            </a>
                                            <a
                                                href="https://play.google.com"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="flex items-center justify-center gap-[8px] bg-[#030213] hover:bg-[#030213]/90 transition-colors rounded-[8px] px-[16px] py-[10px]"
                                            >
                                                <GooglePlayIcon className="text-white w-5 h-5" />
                                                <span className="font-['Poppins',_sans-serif] text-[13px] text-white">{t('dashboard:googlePlay')}</span>
                                            </a>
                                        </div>
                                    </div>
                                </div>
                            </aside>

                            {/* ── Main content ── */}
                            <div className="flex-1 min-w-0">

                                {/* Meta */}
                                <div className="flex items-center gap-[12px] mb-[16px]">
                                    <div className="flex items-center gap-[6px]">
                                        <Calendar className="h-4 w-4 text-[#717182]" />
                                        <span className="font-['Poppins',_sans-serif] text-[12px] text-[#717182]">
                                            {formatDate(article.date)}
                                        </span>
                                    </div>
                                    <div className="flex items-center gap-[6px]">
                                        <User className="h-4 w-4 text-[#717182]" />
                                        <span className="font-['Poppins',_sans-serif] text-[12px] text-[#717182]">
                                            {t('dashboard:equipeHorodaty')}
                                        </span>
                                    </div>
                                </div>

                                {/* Title */}
                                <h2 className="font-['Poppins',_sans-serif] font-normal text-[24px] text-[#030213] mb-[16px] leading-tight">
                                    {article.title}
                                </h2>

                                {/* Markdown body */}
                                <ReactMarkdown components={markdownComponents}>
                                    {article.content}
                                </ReactMarkdown>

                                {/* Mobile CTA */}
                                <div className="mt-[32px] mb-[20px] bg-[#030213] rounded-[16px] p-[24px] text-center md:hidden">
                                    <h3 className="font-['Poppins',_sans-serif] font-normal text-[20px] text-white mb-[12px]">
                                        {t('dashboard:pretACertifierVosPhotos')}
                                    </h3>
                                    <p className="font-['Poppins',_sans-serif] text-[14px] text-white/70 mb-[20px]">
                                        {t('dashboard:commencezDesMaintenantAProtegerVosBiensAvec')}
                                    </p>
                                    <div className="flex flex-col gap-[8px]">
                                        <a
                                            href="https://apps.apple.com"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="w-full flex items-center justify-center gap-[8px] bg-[#34DCA7] rounded-[12px] px-[24px] py-[16px] hover:opacity-90 transition-opacity"
                                        >
                                            <Camera className="h-5 w-5 text-[#030213]" />
                                            <span className="font-['Poppins',_sans-serif] font-normal text-[16px] text-[#030213]">{t('dashboard:appStore')}</span>
                                        </a>
                                        <a
                                            href="https://play.google.com"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="w-full flex items-center justify-center gap-[8px] bg-white/10 rounded-[12px] px-[24px] py-[16px] hover:opacity-90 transition-opacity"
                                        >
                                            <Camera className="h-5 w-5 text-white" />
                                            <span className="font-['Poppins',_sans-serif] font-normal text-[16px] text-white">{t('dashboard:googlePlay')}</span>
                                        </a>
                                    </div>
                                </div>

                            </div>
                        </div>
                    </article>
                </div>
            </div>
        </div>
    );
}
